import type { Rect } from "../scene/geometry";
import type { ClipCutout, OverlayClip, Timeline, VideoClip } from "../scene/schema";
import { type ClipLayout, layoutClips, overlayClips } from "./timeline";
import { stackKey } from "./tracks";

/**
 * "Person freistellen" for videos (docs/05-video-und-untertitel.md): the
 * worker cuts the person out of every frame of a video file once (a matte,
 * apps/backend/src/media/matte.ts). A clip with `cutout` then draws the
 * person as its own layer, so texts can go between the background and the
 * person (elements with `behindCutout`).
 *
 * Order from bottom to top, the same in the editor and the export
 * (`stackKey`, video/tracks.ts): track by track, the pictures of the clips
 * (for cut-out clips their background – "keep": the whole picture,
 * "remove": nothing, on the main track the page colour or the blurred
 * copy), then the persons, then the elements of that track. The elements
 * with `behindCutout` go between the pictures and the persons of the lowest
 * cut-out track (`cutoutTrack`).
 *
 * So B-roll on a track above the speaker still covers the speaker.
 */

/** Track number of the main track (upper tracks start at 1). */
export const MAIN_TRACK = 0;

/** The cut-out settings of a clip, or null (stills and clips in a device never have one). */
export function clipCutout(
  clip: Pick<VideoClip, "kind" | "cutout"> & { device?: unknown },
): ClipCutout | null {
  if (clip.kind !== "video" || !clip.cutout || clip.device) return null;
  return clip.cutout;
}

/** Track of the lowest clip with a cut-out person (main track: 0), null without any. */
export function cutoutTrack(
  timeline: Pick<Timeline, "clips" | "overlays"> | undefined,
): number | null {
  if (!timeline) return null;
  if (timeline.clips.some((clip) => clipCutout(clip))) return MAIN_TRACK;
  let lowest: number | null = null;
  for (const clip of overlayClips(timeline)) {
    if (clipCutout(clip) && (lowest === null || clip.track < lowest)) lowest = clip.track;
  }
  return lowest;
}

/** Whether a document cuts out any person (asset ids of those clips). */
export function cutoutAssetIds(
  timeline: Pick<Timeline, "clips" | "overlays"> | undefined,
): string[] {
  const ids = new Set<string>();
  for (const clip of [...(timeline?.clips ?? []), ...overlayClips(timeline)]) {
    if (clipCutout(clip)) ids.add(clip.assetId);
  }
  return [...ids];
}

/**
 * The person of a main track clip as a clip of track 0: the same picture
 * (box, framing, colour), silent (the main track plays the sound). Only
 * outside transitions – during one both clips show without a person layer
 * (the export renders the main track's persons segment by segment).
 */
export function mainPersonClip(
  entry: ClipLayout,
  next: ClipLayout | undefined,
  frame: { width: number; height: number },
): OverlayClip {
  const { clip } = entry;
  const head = entry.overlap;
  const tail = next?.overlap ?? 0;
  return {
    id: clip.id,
    ...(clip.name ? { name: clip.name } : {}),
    kind: "video",
    assetId: clip.assetId,
    track: MAIN_TRACK,
    start: entry.start + head,
    sourceIn: clip.sourceIn + head * clip.speed,
    sourceOut: clip.sourceOut - tail * clip.speed,
    speed: clip.speed,
    volume: 0,
    muted: true,
    fadeIn: clip.fadeIn,
    fadeOut: clip.fadeOut,
    box: clip.box ?? { x: 0, y: 0, width: frame.width, height: frame.height },
    framing: clip.framing,
    opacity: 1,
    radius: clip.radius ?? 0,
    ...(clip.color ? { color: clip.color } : {}),
    ...(clip.cutout ? { cutout: clip.cutout } : {}),
  };
}

/** The persons of all cut-out main track clips. */
export function mainPersonClips(
  timeline: Pick<Timeline, "clips"> | undefined,
  frame: { width: number; height: number },
): OverlayClip[] {
  const layout = layoutClips(timeline);
  return layout
    .filter((entry) => clipCutout(entry.clip))
    .map((entry) => mainPersonClip(entry, layout[entry.index + 1], frame))
    .filter((person) => person.sourceOut > person.sourceIn);
}

/** Something drawn from a clip of an upper track (or a main track clip's person). */
export interface StackItem {
  /** "picture": the clip as usual (for cut-out clips: their background), "person": the cut-out person. */
  part: "picture" | "person";
  clip: OverlayClip;
  /** Place in the drawing order (`stackKey`). */
  key: number;
}

/**
 * Upper track clips and persons in drawing order. `clips` are the upper
 * track clips, `persons` the main track persons (`mainPersonClips`); pass
 * the ones visible at a time or all of them.
 */
export function stackItems(
  clips: readonly OverlayClip[],
  persons: readonly OverlayClip[],
  /** Where the persons of the lowest cut-out track go (`personLift`). */
  lift: { split: number; track: number } | null = null,
): StackItem[] {
  const items: { item: StackItem; order: number }[] = [];
  const add = (part: StackItem["part"], clip: OverlayClip, track: number) =>
    items.push({
      item: {
        part,
        clip,
        key:
          part === "person"
            ? stackKey(lift && track === lift.split ? lift.track : track, "person")
            : stackKey(track, "picture"),
      },
      order: items.length,
    });
  for (const clip of persons) add("person", clip, MAIN_TRACK);
  for (const clip of clips) {
    const cutout = clipCutout(clip);
    if (cutout?.background !== "remove") add("picture", clip, clip.track);
    if (cutout) add("person", clip, clip.track);
  }
  items.sort((a, b) => a.item.key - b.item.key || a.order - b.order);
  return items.map(({ item }) => item);
}

// ---------------------------------------------------------------------------
// Matte file (worker output)
// ---------------------------------------------------------------------------

/** Empty pixels between picture and matte (H.264 blocks and filters never mix the two). */
export const MATTE_GAP = 16;

/** Where the picture and its matte lie in the matte video (pixels). */
export interface MatteLayout {
  width: number;
  height: number;
  color: Rect;
  alpha: Rect;
}

/**
 * Picture and matte side by side in one frame, so both always come from the
 * same frame: next to each other for upright videos, on top of each other
 * for landscape ones (the frame stays within 4096 px for decoders). The
 * matte is a grey picture (white = person). `width`/`height` are even.
 */
export function matteLayout(width: number, height: number): MatteLayout {
  const color = { x: 0, y: 0, width, height };
  if (width <= height) {
    return {
      width: width * 2 + MATTE_GAP,
      height,
      color,
      alpha: { x: width + MATTE_GAP, y: 0, width, height },
    };
  }
  return {
    width,
    height: height * 2 + MATTE_GAP,
    color,
    alpha: { x: 0, y: height + MATTE_GAP, width, height },
  };
}

/** The layout as fractions of the file (the editor preview is a scaled copy). */
export function matteFractions(layout: MatteLayout): { color: Rect; alpha: Rect } {
  const scale = (rect: Rect): Rect => ({
    x: rect.x / layout.width,
    y: rect.y / layout.height,
    width: rect.width / layout.width,
    height: rect.height / layout.height,
  });
  return { color: scale(layout.color), alpha: scale(layout.alpha) };
}

/** Why the person of a video cannot be cut out, or null when it can. */
export function matteBlocker(
  asset: {
    kind: string;
    status: string;
    width: number | null;
    height: number | null;
    durationMs: number | null;
  },
  maxSeconds: number,
): string | null {
  if (asset.kind !== "video") return "Nur Videos lassen sich so freistellen.";
  if (asset.status === "processing") return "Das Video wird noch verarbeitet.";
  if (asset.status !== "ready" || !asset.width || !asset.height || !asset.durationMs) {
    return "Das Video konnte nicht gelesen werden.";
  }
  if (asset.durationMs > maxSeconds * 1000 + 999) {
    return `Das Video ist zu lang zum Freistellen (höchstens ${Math.round(maxSeconds / 60)} Minuten).`;
  }
  return null;
}
